import React, { useState, useEffect } from 'react';
import { History, GitBranch, Eye, Download, Trash2, Star, ArrowRight, ArrowLeft, Plus } from 'lucide-react';
import { VersionService, type CreativeVersion, type VersionStats, type VersionComparison } from '../lib/versions';
import { formatDistanceToNow } from 'date-fns';
import EnhancedVersionComparison from './EnhancedVersionComparison';

interface VersionHistoryProps {
  creativeId: string;
  onVersionChange?: (version: CreativeVersion) => void;
  onCreateNewVersion?: () => void;
}

export default function VersionHistory({ creativeId, onVersionChange, onCreateNewVersion }: VersionHistoryProps) {
  const [versions, setVersions] = useState<CreativeVersion[]>([]);
  const [stats, setStats] = useState<VersionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVersions, setSelectedVersions] = useState<string[]>([]);
  const [comparison, setComparison] = useState<VersionComparison | null>(null);
  const [showComparison, setShowComparison] = useState(false);

  useEffect(() => {
    loadVersionData();
  }, [creativeId]);

  const loadVersionData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [versionsData, statsData] = await Promise.all([
        VersionService.getVersionHistory(creativeId),
        VersionService.getVersionStats(creativeId)
      ]);
      
      setVersions(versionsData);
      setStats(statsData);
    } catch (err) {
      console.error('Failed to load version data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load version data');
    } finally {
      setLoading(false);
    }
  };

  const handleSetCurrentVersion = async (versionId: string) => {
    try {
      await VersionService.setCurrentVersion(versionId);
      await loadVersionData(); // Reload to update current version status
      
      const newCurrentVersion = versions.find(v => v.id === versionId);
      if (newCurrentVersion && onVersionChange) {
        onVersionChange(newCurrentVersion);
      }
    } catch (err) {
      console.error('Failed to set current version:', err);
      setError(err instanceof Error ? err.message : 'Failed to set current version');
    }
  };

  const handleDeleteVersion = async (versionId: string) => {
    if (!confirm('Are you sure you want to delete this version? This action cannot be undone.')) {
      return;
    }

    try {
      await VersionService.deleteVersion(versionId);
      await loadVersionData(); // Reload to update the list
    } catch (err) {
      console.error('Failed to delete version:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete version');
    }
  };

  const handleVersionSelection = (versionId: string) => {
    setSelectedVersions(prev => {
      if (prev.includes(versionId)) {
        return prev.filter(id => id !== versionId);
      } else if (prev.length < 2) {
        return [...prev, versionId];
      } else {
        // Replace the first selected version
        return [prev[1], versionId];
      }
    });
  };

  const handleCompareVersions = async () => {
    if (selectedVersions.length !== 2) return;

    try {
      const comparisonData = await VersionService.compareVersions(selectedVersions[0], selectedVersions[1]);
      setComparison(comparisonData);
      setShowComparison(true);
    } catch (err) {
      console.error('Failed to compare versions:', err);
      setError(err instanceof Error ? err.message : 'Failed to compare versions');
    }
  };

  const getVersionBadgeColor = (version: CreativeVersion) => {
    if (version.isCurrentVersion) return 'bg-green-100 text-green-800';
    return 'bg-gray-100 text-gray-800';
  };

  const getScoreBadgeColor = (score: number) => {
    if (score >= 80) return 'bg-green-100 text-green-800';
    if (score >= 60) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown';
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-800">Error: {error}</p>
        <button 
          onClick={loadVersionData}
          className="mt-2 text-red-600 hover:text-red-800 underline"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center space-x-2">
          <History className="h-5 w-5 text-gray-600" />
          <h3 className="text-base sm:text-lg font-medium text-gray-900">Version History</h3>
          {stats && (
            <span className="text-xs sm:text-sm text-gray-500">
              ({stats.totalVersions} version{stats.totalVersions !== 1 ? 's' : ''})
            </span>
          )}
        </div>
        
        {onCreateNewVersion && (
          <button
            onClick={onCreateNewVersion}
            className="flex items-center justify-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm min-h-[44px] w-full sm:w-auto"
          >
            <Plus className="h-4 w-4" />
            <span>New Version</span>
          </button>
        )}
      </div>

      {/* Version Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{stats.totalVersions}</div>
            <div className="text-sm text-gray-600">Total Versions</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">v{stats.currentVersion}</div>
            <div className="text-sm text-gray-600">Current</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{stats.averageScore}%</div>
            <div className="text-sm text-gray-600">Avg Score</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">{stats.totalAnalyses}</div>
            <div className="text-sm text-gray-600">Analyses</div>
          </div>
        </div>
      )}

      {/* Comparison Controls */}
      {selectedVersions.length > 0 && (
        <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="text-sm text-blue-800">
            {selectedVersions.length === 1 ? 
              'Select one more version to compare' : 
              'Two versions selected for comparison'
            }
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setSelectedVersions([])}
              className="text-blue-600 hover:text-blue-800 text-sm underline"
            >
              Clear Selection
            </button>
            {selectedVersions.length === 2 && (
              <button
                onClick={handleCompareVersions}
                className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
              >
                Compare
              </button>
            )}
          </div>
        </div>
      )}

      {/* Version List */}
      <div className="space-y-3">
        {versions.map((version) => {
          const isSelected = selectedVersions.includes(version.id);
          const analysisData = version.analysisData as any;
          const overallScore = analysisData?.overallScore;
          
          return (
            <div
              key={version.id}
              className={`p-4 border rounded-lg transition-all ${
                isSelected ? 'border-blue-300 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start space-x-2 sm:space-x-3 flex-1 min-w-0">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleVersionSelection(version.id)}
                    className="mt-1 h-4 w-4 text-blue-600 rounded border-gray-300 flex-shrink-0"
                  />
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-2 flex-wrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getVersionBadgeColor(version)} flex-shrink-0`}>
                        v{version.versionNumber}
                        {version.isCurrentVersion && ' (Current)'}
                      </span>
                      
                      {version.versionName && (
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {version.versionName}
                        </span>
                      )}
                      
                      {stats?.bestVersion?.version === version.versionNumber && (
                        <Star className="h-4 w-4 text-yellow-500 flex-shrink-0" fill="currentColor" />
                      )}
                    </div>
                    
                    <h4 className="font-medium text-gray-900 mb-1 truncate">{version.name}</h4>
                    
                    {version.versionNotes && (
                      <p className="text-sm text-gray-600 mb-2 line-clamp-2">{version.versionNotes}</p>
                    )}
                    
                    <div className="flex items-center space-x-2 sm:space-x-4 text-sm text-gray-500 flex-wrap gap-1">
                      <span className="flex-shrink-0">{formatDistanceToNow(new Date(version.createdAt))} ago</span>
                      <span className="flex-shrink-0">{formatFileSize(version.fileSize)}</span>
                      {version.fileType && <span className="flex-shrink-0">{version.fileType}</span>}
                      {overallScore ? (
                        <span className={`px-2 py-1 rounded-full text-xs flex-shrink-0 ${getScoreBadgeColor(overallScore)}`}>
                          {overallScore}% score
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600 flex-shrink-0">
                          No Analysis
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0">
                  <button
                    onClick={() => window.open(version.url, '_blank')}
                    className="p-1.5 sm:p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 min-w-[32px] min-h-[32px] sm:min-w-[36px] sm:min-h-[36px] flex items-center justify-center"
                    title="View"
                  >
                    <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
                  </button>
                  
                  {!version.isCurrentVersion && (
                    <button
                      onClick={() => handleSetCurrentVersion(version.id)}
                      className="p-1.5 sm:p-2 text-blue-400 hover:text-blue-600 rounded-lg hover:bg-blue-50 min-w-[32px] min-h-[32px] sm:min-w-[36px] sm:min-h-[36px] flex items-center justify-center"
                      title="Set as Current"
                    >
                      <GitBranch className="h-3 w-3 sm:h-4 sm:w-4" />
                    </button>
                  )}
                  
                  {versions.length > 1 && !version.isCurrentVersion && (
                    <button
                      onClick={() => handleDeleteVersion(version.id)}
                      className="p-1.5 sm:p-2 text-red-400 hover:text-red-600 rounded-lg hover:bg-red-50 min-w-[32px] min-h-[32px] sm:min-w-[36px] sm:min-h-[36px] flex items-center justify-center"
                      title="Delete Version"
                    >
                      <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Enhanced Comparison Modal */}
      {showComparison && comparison && (
        <EnhancedVersionComparison 
          comparison={comparison} 
          onClose={() => setShowComparison(false)} 
        />
      )}
    </div>
  );
} 